/** What the package lists (screens §P6 Beat 3): one row per changed family, one per answered event, and the limits the preview projects to breach. */
import type { ContentCatalog, CroObjection, DecisionCatalogEntry, DraftView, RoundReport, TeamState } from "@bsim/contracts";
import { CAMELS_COMPONENT_DECISIONS, CAMELS_COMPONENT_KEYS, type Decisions, type DecisionId, type KpiId, type PreviewResult } from "@bsim/domain";
import { INPUT_LIMITS } from "../theme/GxLimitedText";
import { formatKpi, printedKpi } from "../format";
import type { RiskProjection } from "../api/riskFields";
import { kpiLabel } from "../format/labels";
import { kpiOf, limitWarningText, QUARTER_END_LOW_LABEL, shown } from "../decisions/previewRows";
import { isOneRound } from "../decisions/deskSheet";
import { changeLegs, changeTag, changedDecisions, decisionIdOf, decisionTitle, oneRoundLine, optionLabel, readDecision } from "../decisions/decisionValues";
import { deskLabel, deskOrder } from "../decisions/labels";
import { answeredWith, DEFAULT_ID } from "../events/deck";

/** The last rating's measure for each component, from the last released report. */
export type RatedOn = RoundReport["scorecard"]["camels"]["ratedOn"];

/** A changed decision's moved legs, each under its own name when the decision has several ("Existing base: Follow → Lag · New money: Follow → Lag"); null when no leg moved. */
const legsText = (entry: DecisionCatalogEntry, now: Decisions, was: Partial<Decisions>): string | null => {
  const legs = changeLegs(entry, now, was);
  return legs.length === 0 ? null : legs.map((l) => (l.leg === null ? l.change : `${l.leg}: ${l.change}`)).join(" · ");
};

export interface PackageRow {
  readonly id: string;
  readonly label: string;
  readonly change: string;
  readonly rationale: string | null;
  /** Decisions an objection on this row names (families only). */
  readonly decisionIds: readonly DecisionId[];
}

export function packageRows(team: TeamState, draft: DraftView, catalog: ContentCatalog, seed: Partial<Decisions>): PackageRow[] {
  const families = deskOrder(catalog).filter((f) => draft.changedFamilies.includes(f)).map((family): PackageRow => {
    const decisions = changedDecisions(catalog.decisions, family, draft.decisions, seed);
    return {
      id: family, label: deskLabel(catalog, family), rationale: draft.rationales[family]?.text ?? null, decisionIds: decisions.map((d) => d.decisionId),
      change: decisions.map((d) => `${decisionTitle(catalog.decisions, d.decisionId)} ${legsText(d, draft.decisions, seed) ?? Object.entries(d.options).map(([sub, o]) => optionLabel(o, readDecision(draft.decisions, d.decisionId, sub) ?? null)).join(" · ")}${isOneRound(d) && changeTag(d, draft.decisions, seed) !== null ? ` (${oneRoundLine(d)})` : ""}`).join(" · "),
    };
  });
  const events = team.pendingEvents.flatMap((e): PackageRow[] => {
    const choice = e.card.choices.find((c) => c.id === e.response?.choiceId);
    // An answered card names the decisions its answer sets (the draft's card constraints and provenance carry them by "EV-06:B"), so the risk officer can object to a card-only package.
    const ref = `${e.card.eventId}:${e.response?.choiceId ?? ""}`;
    const paths = [...draft.activeConstraints.filter((c) => c.source === "event" && c.sourceRef === ref).map((c) => c.decisionId), ...(draft.provenance ?? []).filter((p) => p.source === "card" && p.by?.includes(ref) === true).map((p) => p.decisionPath)];
    // A card whose answer left no constraint or provenance (a hedge, a sale) falls back to the choice's `relatedDecisions`, so its row accepts an objection.
    const related = choice?.relatedDecisions ?? [];
    const named = [...new Set(paths.map(decisionIdOf))] as DecisionId[];
    const decisionIds = named.length > 0 ? named : [...new Set(related)];
    const answer = answeredWith(e);
    return answer === undefined ? [] : [{ id: e.card.eventId, label: e.card.title, change: answer.id === DEFAULT_ID ? answer.label : `${answer.id} · ${answer.label}`, rationale: e.response?.rationale?.text ?? null, decisionIds }];
  });
  return [...families, ...events];
}

/** What a limit is tested on: the own EVE limit on the lowest quarter-end of the plan's year (the projection's, else the preview's own), nothing else for it; any other measure on the preview's point. */
function testedValue(kpi: KpiId, preview: PreviewResult | null, projection: RiskProjection | undefined): { readonly tested: number; readonly quarterEnd: boolean } | null {
  if (kpi === "eve200") {
    const low = projection?.eve200QuarterEndLow ?? (preview === null ? undefined : shown(kpiOf(preview, kpi))?.quarterEndLow);
    return low === undefined ? null : { tested: low, quarterEnd: true };
  }
  const v = preview === null ? null : shown(kpiOf(preview, kpi));
  return v === null ? null : { tested: v.point, quarterEnd: false };
}

/** The appetite limits the plan lands outside of, with the tested reading, from the draft's one risk projection where it carries the reading. */
function breachedLimits(team: TeamState, preview: PreviewResult | null, projection: RiskProjection | undefined) {
  return (team.strategyStatement?.riskAppetite ?? []).flatMap((r) => {
    const v = testedValue(r.kpi, preview, projection);
    if (v === null) return [];
    const [at, limit] = [printedKpi(r.kpi, v.tested), printedKpi(r.kpi, r.limit)];
    return (r.cmp === "ge" ? at < limit : at > limit) ? [{ r, v }] : [];
  });
}

/** "Reported CET1 ≥ 10.00%: projected 9.80%": the appetite limits the plan lands outside of. */
export function projectedBreaches(team: TeamState, preview: PreviewResult | null, projection?: RiskProjection): string[] {
  return breachedLimits(team, preview, projection).map(({ r, v }) => `${kpiLabel(r.kpi)} ${r.cmp === "ge" ? "≥" : "≤"} ${formatKpi(r.kpi, r.limit)}: ${v.quarterEnd ? QUARTER_END_LOW_LABEL : "projected"} ${formatKpi(r.kpi, v.tested)}`);
}

const MAX_MOVERS = 6;

/**
 * "EVE at +200bp: decisions that move this measure (their size is not shown here): rate position, securities make-up, …": under each breach, the decisions that move that measure (a list, not sized levers). The component is the one the last
 * rating read that measure for (`scorecard.camels.ratedOn[k].kpi`); with no such component (no earlier report, or a measure no component is rated on) nothing is printed.
 */
export function breachMovers(team: TeamState, catalog: ContentCatalog, preview: PreviewResult | null, projection: RiskProjection | undefined, ratedOn: RatedOn | undefined): string[] {
  return breachedLimits(team, preview, projection).flatMap(({ r }) => {
    const key = CAMELS_COMPONENT_KEYS.find((k) => ratedOn?.[k]?.kpi === r.kpi);
    if (key === undefined) return [];
    const titles = [...new Set(CAMELS_COMPONENT_DECISIONS[key].map((id) => decisionTitle(catalog.decisions, id)))];
    return [`${kpiLabel(r.kpi)}: decisions that move this measure (their size is not shown here): ${titles.slice(0, MAX_MOVERS).join(", ")}${titles.length > MAX_MOVERS ? ` and ${titles.length - MAX_MOVERS} more` : ""}`];
  });
}

/** The limits the plan keeps on the central path and breaks on the forecast's adverse one, beside the limits; each reads the projection's own warning. */
export function limitWarningLines(team: TeamState, projection: RiskProjection | undefined): string[] {
  return (projection?.limitWarnings ?? []).filter((w) => (team.strategyStatement?.riskAppetite ?? []).some((r) => r.kpi === w.kpi)).map(limitWarningText);
}

/** An objection is stored with the decisions it names only, and a card row can name the same decisions as a desk row: the row's label opens the words so the objection stays tied to the row it was raised on. */
export const objectionText = (row: Pick<PackageRow, "label">, note: string): string => `${row.label}: ${note.trim()}`;
/** The most the note can run to once the row's label is in front of it (the stored text holds 600). */
export const objectionNoteMax = (row: Pick<PackageRow, "label">): number => Math.max(0, INPUT_LIMITS.croObjection - `${row.label}: `.length);

/**
 * The severity of the open objection on each row, by row id. An objection marks the rows whose decisions are exactly its own, else the rows sharing a decision with it; when more than one row
 * qualifies, only the one whose label opens the objection's words is marked (so an objection on a card never marks the desk that holds the same decision, or the reverse).
 */
export function objectedRows(rows: readonly PackageRow[], objections: TeamState["objections"]): Record<string, CroObjection["severity"]> {
  return Object.fromEntries(objections.filter((x) => x.status === "open").flatMap((o) => {
    const same = rows.filter((r) => r.decisionIds.length === o.decisionIds.length && o.decisionIds.every((d) => r.decisionIds.includes(d)));
    const near = same.length > 0 ? same : rows.filter((r) => o.decisionIds.some((d) => r.decisionIds.includes(d)));
    const named = near.filter((r) => (o.text ?? "").startsWith(`${r.label}: `));
    return (near.length > 1 && named.length > 0 ? named : near).map((r) => [r.id, o.severity] as const);
  }));
}
